import React from 'react';
import { Cloud, HardDrive, Youtube, Archive, User as UserIcon } from 'lucide-react';
import { MarketingContent } from '../../types/marketing';
import { WorkflowTemplate } from '../../utils/marketingTemplates';
import './MarketingCard.css';

interface MarketingCardProps {
  item: MarketingContent;
  template: WorkflowTemplate;
  onClick: () => void;
  animDelay?: number;
}

function relativeTime(date: Date): string {
  const ms = Date.now() - date.getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(months / 12);
  return `${years}y ago`;
}

const MarketingCard: React.FC<MarketingCardProps> = ({ item, template, onClick, animDelay = 0 }) => {
  const stage = template.stages.find(s => s.id === item.currentStageId) ?? template.stages[0];
  const stageStatus = item.stageStatuses?.[item.currentStageId]?.status ?? 'todo';
  const updated = item.updatedAt?.toDate?.() ?? null;

  const links: { label: string; icon: React.ReactNode; url: string }[] = [];
  if (item.dropboxRawUrl) links.push({ label: 'Dropbox raw', icon: <Cloud size={11} />, url: item.dropboxRawUrl });
  if (item.ssdPath) links.push({ label: 'SSD', icon: <HardDrive size={11} />, url: '' });
  if (item.youtubeUrl) links.push({ label: 'YouTube', icon: <Youtube size={11} />, url: item.youtubeUrl });
  if (item.dropboxArchiveUrl) links.push({ label: 'Archive', icon: <Archive size={11} />, url: item.dropboxArchiveUrl });

  return (
    <div
      className="mh-card"
      onClick={onClick}
      style={{ animationDelay: `${animDelay}ms` }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="mh-card-top">
        <span
          className="mh-card-stage-dot"
          style={{ background: stage.color }}
          aria-hidden="true"
        />
        <span className="mh-card-stage-label">{stage.label}</span>
        {stageStatus === 'in-progress' && (
          <span className="mh-card-active-pulse" aria-label="In progress" />
        )}
      </div>

      <div className="mh-card-title">{item.title}</div>

      {item.guestName && (
        <div className="mh-card-guest">
          <UserIcon size={11} />
          <span>{item.guestName}</span>
        </div>
      )}

      <div className="mh-card-bottom">
        <div className="mh-card-links">
          {links.length === 0 ? (
            <span className="mh-card-no-links">No links yet</span>
          ) : (
            links.map((l, i) =>
              l.url ? (
                <a
                  key={i}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mh-card-link"
                  title={l.label}
                  onClick={e => e.stopPropagation()}
                  aria-label={l.label}
                >
                  {l.icon}
                </a>
              ) : (
                <span key={i} className="mh-card-link mh-card-link-static" title={l.label} aria-label={l.label}>
                  {l.icon}
                </span>
              )
            )
          )}
        </div>
        {updated && (
          <span className="mh-card-updated">{relativeTime(updated)}</span>
        )}
      </div>
    </div>
  );
};

export default MarketingCard;
